import { useState, useEffect, useRef } from 'react';
import type { Room } from '../types';
import type { LocationData } from '../types';
import { useAuthStore, useMessagesStore, usePresenceStore, useRoomsStore } from '../store';
import { useWebSocket } from '../hooks/useWebSocket';
import { api } from '../api/client';
import { MessageBubble } from './MessageBubble';

function TypingIndicator({ names }: { names: string[] }) {
  if (names.length === 0) return null;
  const label = names.length === 1 ? `${names[0]} sedang mengetik…` : `${names.slice(0, 2).join(', ')} sedang mengetik…`;
  return (
    <div className="flex items-center gap-2 px-4 py-1 animate-fade-in">
      <div className="flex gap-1">
        <div className="typing-dot" />
        <div className="typing-dot" />
        <div className="typing-dot" />
      </div>
      <span className="text-xs text-dark-400">{label}</span>
    </div>
  );
}

interface ChatWindowProps {
  room: Room;
  onOpenGroupInfo: () => void;
  onBack?: () => void;
}

export function ChatWindow({ room, onOpenGroupInfo, onBack }: ChatWindowProps) {
  const { user } = useAuthStore();
  const { messagesByRoom, hasMoreByRoom, nextCursorByRoom, setMessages, prependMessages } = useMessagesStore();
  const { typingUsers } = usePresenceStore();
  const { clearUnread } = useRoomsStore();

  const messages = messagesByRoom[room.id] || [];
  const hasMore = hasMoreByRoom[room.id] || false;
  const nextCursor = nextCursorByRoom[room.id] || null;

  const [input, setInput] = useState('');
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { sendMessage, sendTyping, sendRead } = useWebSocket({
    roomId: room.id,
  });

  // Load initial messages
  useEffect(() => {
    if (!messagesByRoom[room.id]) {
      api.messages.list(room.id).then(({ messages, hasMore, nextCursor }) => {
        setMessages(room.id, messages, hasMore, nextCursor);
      }).catch(console.error);
    }
    clearUnread(room.id);
  }, [room.id]);

  // Auto-scroll on new messages
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;
    if (isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  // Mark last message as read
  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    if (lastMsg && lastMsg.user_id !== user?.id) {
      sendRead(lastMsg.id);
    }
  }, [messages.length]);

  // Load more (scroll to top)
  const handleLoadMore = async () => {
    if (loadingHistory || !hasMore || !nextCursor) return;
    setLoadingHistory(true);
    const prevScrollHeight = scrollContainerRef.current?.scrollHeight || 0;
    try {
      const { messages: older, hasMore: moreAvail, nextCursor: cursor } = await api.messages.list(room.id, nextCursor);
      prependMessages(room.id, older, moreAvail, cursor);
      // Preserve scroll position
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight - prevScrollHeight;
        }
      });
    } finally {
      setLoadingHistory(false);
    }
  };

  // Handle typing indicator
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      sendTyping(true);
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      isTypingRef.current = false;
      sendTyping(false);
    }, 1500);
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    sendMessage(text, 'text');
    setInput('');
    isTypingRef.current = false;
    sendTyping(false);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Image upload
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url, name, size } = await api.files.upload(file);
      sendMessage(url, 'image', { fileUrl: url, fileName: name, fileSize: size });
    } catch (err) {
      alert('Gagal upload: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Location sharing
  const handleShareLocation = () => {
    if (!navigator.geolocation) {
      alert('Browser tidak mendukung geolocation');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc: LocationData = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        sendMessage(JSON.stringify(loc), 'location');
      },
      (err) => alert('Gagal mendapat lokasi: ' + err.message)
    );
  };

  const getRoomDisplayName = () => {
    if (room.type === 'personal') return room.partner?.name || 'Chat Pribadi';
    return room.name || 'Grup';
  };

  const typingList = (typingUsers[room.id] || []).filter(u => u.userId !== user?.id).map(u => u.userName);

  return (
    <div className="flex flex-col h-full bg-dark-900">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-dark-700 bg-dark-800">
        <div className="flex-1 flex items-center gap-3 min-w-0">
          {onBack && (
            <button
              onClick={onBack}
              className="sm:hidden p-2 -ml-2 rounded-xl text-dark-300 hover:text-white hover:bg-white/[0.06] transition-all active:scale-95 flex-shrink-0"
              aria-label="Kembali"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {room.type === 'group' ? (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold flex-shrink-0">
              {(room.name || 'G')[0]}
            </div>
          ) : room.partner?.avatar_url ? (
            <img src={room.partner.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" referrerPolicy="no-referrer" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-500 to-cyan-500 flex items-center justify-center text-white font-bold flex-shrink-0">
              {(room.partner?.name || '?')[0]}
            </div>
          )}
          <div className="min-w-0">
            <h2 className="font-semibold text-white truncate">{getRoomDisplayName()}</h2>
            <p className="text-xs text-dark-400">
              {room.type === 'group' ? `${room.member_count || 0} anggota` : 'Personal'}
            </p>
          </div>
        </div>
        {room.type === 'group' && (
          <button
            onClick={onOpenGroupInfo}
            className="p-2 rounded-xl hover:bg-white/[0.06] text-dark-300 hover:text-white transition-all"
            title="Info grup"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
        {/* Load more button */}
        {hasMore && (
          <div className="flex justify-center pb-2">
            <button
              onClick={handleLoadMore}
              disabled={loadingHistory}
              className="px-4 py-1.5 text-xs text-dark-400 hover:text-white bg-dark-700 hover:bg-dark-600 rounded-full transition-all disabled:opacity-50"
            >
              {loadingHistory ? 'Memuat…' : 'Muat pesan lama'}
            </button>
          </div>
        )}

        {messages.length === 0 && !loadingHistory ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-20">
            <div className="text-6xl mb-4">👋</div>
            <p className="text-dark-400">Mulai percakapan dengan mengirim pesan!</p>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const prev = messages[idx - 1];
            const showAvatar = !prev || prev.user_id !== msg.user_id ||
              msg.created_at - prev.created_at > 300;
            const isMine = msg.user_id === user?.id;
            return (
              <MessageBubble
                key={msg.id}
                message={msg}
                isMine={isMine}
                showAvatar={showAvatar && !isMine}
                onImageClick={(url) => setImagePreview(url)}
              />
            );
          })
        )}

        <TypingIndicator names={typingList} />
        <div ref={messagesEndRef} />
      </div>

      {/* Image lightbox */}
      {imagePreview && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 animate-fade-in"
          onClick={() => setImagePreview(null)}
        >
          <img src={imagePreview} alt="preview" className="max-w-[90vw] max-h-[90vh] rounded-2xl object-contain" />
          <button
            onClick={() => setImagePreview(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Input area */}
      <div className="px-3 sm:px-4 pt-2.5 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] border-t border-dark-700 bg-dark-800 flex-shrink-0">
        <div className="flex items-end gap-1.5 sm:gap-2">
          {/* Attach file */}
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="p-2.5 rounded-xl text-dark-400 hover:text-white hover:bg-white/[0.06] transition-all flex-shrink-0 disabled:opacity-50 active:scale-95"
            title="Kirim foto"
          >
            {uploading ? (
              <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            )}
          </button>

          {/* Location */}
          <button
            onClick={handleShareLocation}
            className="p-2.5 rounded-xl text-dark-400 hover:text-white hover:bg-white/[0.06] transition-all flex-shrink-0 active:scale-95"
            title="Kirim lokasi"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>

          {/* Text input */}
          <textarea
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Ketik pesan…"
            rows={1}
            className="flex-1 bg-dark-700 text-white placeholder-dark-400 rounded-2xl px-3.5 sm:px-4 py-2.5 sm:py-3 text-base sm:text-sm resize-none outline-none focus:ring-2 focus:ring-brand-500/30 transition-all"
            style={{ maxHeight: 120, overflowY: 'auto' }}
          />

          {/* Send */}
          <button
            onClick={handleSend}
            disabled={!input.trim()}
            className="p-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white transition-all flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
